import { FC, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  Label,
  RadixTable,
  RadioGroup,
  RadioGroupItem,
} from '@features/ui';
import {
  TReportUserDemographicsData,
  TReportUserDemographicsParams,
} from '@src/types/reports';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { toast } from 'react-toastify';
import {
  exportUserDemographicsReport,
  getUserDemographicsReport,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportUserDemographicsData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] min-w-[150px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-white font-semibold mb-1">
          {payload[0]?.payload?.location}
        </p>
        <p className="text-[#B6D72C] text-lg font-bold">
          {payload[0]?.value?.toLocaleString()}
        </p>
        <p className="text-[#999999] text-[10px] mt-1">Users</p>
      </div>
    );
  }

  return null;
};

export const UserDemoGraphics: FC = () => {
  const { auth } = useAuthStore();
  const [groupBy, setGroupBy] = useState<'country' | 'state'>('country');
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TReportUserDemographicsData[]>([]);

  const customColumns = [
    {
      title: 'User Demographics',
      className: 'text-right',
      render: (item?: TReportUserDemographicsData) => {
        if (!item) return null;

        return (
          <p className="text-white">{item.user_count?.toLocaleString()}</p>
        );
      },
    },
  ];

  const { columns, tableData } =
    useReportTableColumns<TReportUserDemographicsData>({
      data,
      selectedItems: [],
      compareToPreviousPeriod: false,
      compareToSamePeriodPreviousYear: false,
      getAccessField: (item) => item.location,
      getMainDataField: (item) => item.user_count,
      customColumns,
      includeTotalsRow: true,
      totalsRowLabel: 'Total',
    });

  const handleRunReport = async () => {
    setIsLoading(true);
    try {
      const params: TReportUserDemographicsParams = {
        group_by: groupBy,
      };

      const response = await getUserDemographicsReport(params);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const params: TReportUserDemographicsParams = {
        group_by: groupBy,
      };

      const response = await exportUserDemographicsReport(params);

      downloadFile(response.data, 'user-demographics-report');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">User Demographics</h1>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white text-sm font-medium">Group By</Label>
            <RadioGroup
              value={groupBy}
              onValueChange={(value) =>
                setGroupBy(value as 'country' | 'state')
              }
            >
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem id="country" value="country" />
                  <Label
                    className="text-sm font-normal cursor-pointer"
                    htmlFor="country"
                  >
                    Country
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem id="state" value="state" />
                  <Label
                    className="text-sm font-normal cursor-pointer"
                    htmlFor="state"
                  >
                    State
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="flex items-center">
            <Button
              className="bg-[#B6D72C] text-black hover:bg-[#9fb824] px-6"
              disabled={isLoading}
              onClick={handleRunReport}
            >
              {isLoading ? 'Running...' : 'Run Report'}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">
            User Distribution by {groupBy === 'country' ? 'Country' : 'State'}
          </h2>
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                angle={-45}
                dataKey="location"
                height={100}
                stroke="#999"
                style={{ fontSize: '12px' }}
                textAnchor="end"
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="rect" wrapperStyle={{ fontSize: '14px' }} />
              <Bar
                dataKey="user_count"
                fill="#B6D72C"
                name="User Demographics"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
